/**
 * Every search filter, defined once.
 *
 * `FILTER_DEFS` is a total map over `FilterParams`: one entry per field, saying
 * what kind of value it holds, which URL param carries it, which sidebar group
 * counts and clears it, and which backend request key it becomes. The generic
 * code paths are driven from it — URL parse (SSR and the client's URL-or-preset
 * merge), URL serialise, the empty default, the saved-search zod schema, the
 * simple part of the backend request (`buildBackendFilters` and the SSR
 * forwarding in scripts/search-queries.ts), the sidebar's staged state, its
 * per-group counts and Clear buttons, and the search_performed filter count.
 *
 * Adding a search filter (e.g. a "Hat LEI" checkbox → `?hasLei=true` →
 * backend `hasLei: true`):
 *   1. lib/search-filter-params.ts — the field on `FilterParams`.
 *   2. FILTER_DEFS below — `{ kind, url, group, backend }`. A simple kind
 *      (flag / triState / text / list) needs nothing else in lib/.
 *   3. lib/backend-api/types/search.ts — the key on
 *      `AdvancedCompanySearchRequest`; `backend` is type-checked against it
 *      and `CompanySearchParams` picks it up from there.
 *   4. The control's JSX in its group (new_components/searchresults/
 *      filter-groups/*Parts.tsx), reading `staged.<key>` and writing
 *      `set.<key>`; a profile checkbox is one `PROFILE_FLAGS` entry in
 *      ProfileFilterGroupParts.tsx. Nothing to wire in SidebarFilterGroups.
 *   5. Its chip in new_components/searchresults/activeFilters.ts (`FILTER_SPEC`
 *      is total over FilterParams, so tsc asks for it).
 *   6. i18n/messages/en.json + de.json: the control label and the chip text.
 *   Gated? Add it to CONTROL_TIER and GATED_BACKEND_KEYS (and GROUP_CONTROLS)
 *   in lib/search-gating.ts. Then `npm run check:filter-chips` and `npm test`
 *   (lib/search-filters.golden.test.cjs pins every existing filter's URL,
 *   request and saved-search behaviour; a new filter leaves it passing).
 *
 * `backend: 'custom'` marks the fields whose request mapping is hand-written
 * in `buildBackendFilters` (range objects, the WZ sector split, founding-year
 * dates, the joined city string, …); their URL and sidebar handling is still
 * generic unless the entry says otherwise.
 *
 * Entry order is the order of the URL's query string — the fiscal year leads
 * because it always has — so keep a new entry where its param should appear.
 */
import { isCompanyCountryCode } from './countries';
import type { AdvancedCompanySearchRequest } from './backend-api/types/search';
import type { FilterParams } from './search-filter-params';

export type FilterKey = keyof FilterParams;

/** The sidebar groups, by the id their FilterGroup renders with. */
export type FilterGroupId = 'profile' | 'location' | 'industry' | 'size' | 'people' | 'register';

export const FILTER_GROUP_IDS: readonly FilterGroupId[] = [
  'location', 'industry', 'size', 'profile', 'people', 'register',
];

/**
 * - `flag` — boolean, `false` = no filter; URL `?x=true`.
 * - `triState` — `true` / `false` / `undefined` (no filter); URL `?x=true|false`.
 * - `text` — string, `''` = no filter.
 * - `list` — string[], repeated URL param, `[]` = no filter.
 * - `bound` — one side of a numeric interval (`<metric>Min` / `<metric>Max`),
 *   with the legacy `<metric>Range` bucket ids accepted from URLs.
 * - `year` — the filing year: sanitised, serialised as `year`.
 */
export type FilterKind = 'flag' | 'triState' | 'text' | 'list' | 'bound' | 'year';

type KindOf<V> = [V] extends [boolean]
  ? 'flag'
  : [V] extends [boolean | undefined]
    ? 'triState'
    : [V] extends [string]
      ? 'text'
      : [V] extends [string[]]
        ? 'list'
        : [V] extends [number | undefined]
          ? 'bound' | 'year'
          : never;

type Req = Required<AdvancedCompanySearchRequest>;

/** Request keys whose type can carry this field's value. */
type BackendKeyFor<K extends FilterKey> = {
  [B in keyof Req]: NonNullable<FilterParams[K]> extends Req[B] ? B : never;
}[keyof Req];

/** Read access to URL params, normalised over Next's record and URLSearchParams. */
export interface FilterURLParams {
  has(key: string): boolean;
  /** The first value, or '' when absent. */
  get(key: string): string;
  getAll(key: string): string[];
}

export interface FilterDef<K extends FilterKey> {
  kind: KindOf<FilterParams[K]>;
  /** URL query param. */
  url: string;
  /** Sidebar group whose header counts it and whose Clear resets it. */
  group: FilterGroupId | null;
  /** Request key for the generic mapping, or 'custom' (hand-written). */
  backend: BackendKeyFor<K> | 'custom';
  /** Value sent instead of the field's own (the kind decides whether). */
  toBackend?: (value: NonNullable<FilterParams[K]>) => NonNullable<FilterParams[K]>;
  /** Replaces the kind's URL read: the URL's value, else `fallback`. */
  read?: (params: FilterURLParams, fallback: FilterParams[K]) => FilterParams[K];
  /** Replaces the kind's URL write. */
  write?: (params: URLSearchParams, filters: FilterParams) => void;
  /** For `bound`: the interval it is one side of. */
  metric?: 'revenue' | 'aktiva' | 'profit' | 'employees';
  /** Modifies another filter; never counted as a filter of its own. */
  modifier?: true;
  /** Left out of the search_performed filter count (pre-existing). */
  notInSubmittedCount?: true;
}

export type FilterDefs = { readonly [K in FilterKey]-?: FilterDef<K> };

/** A definition seen without its key: what the generic engines iterate. */
export interface AnyFilterDef {
  kind: FilterKind;
  url: string;
  group: FilterGroupId | null;
  backend: string;
  toBackend?: (value: never) => unknown;
  read?: (params: FilterURLParams, fallback: never) => unknown;
  write?: (params: URLSearchParams, filters: FilterParams) => void;
  metric?: FilterDef<FilterKey>['metric'];
  modifier?: true;
  notInSubmittedCount?: true;
}

export const FILTER_DEFS = {
  financialYear: { kind: 'year', url: 'year', group: 'size', backend: 'custom', notInSubmittedCount: true },
  q: { kind: 'text', url: 'q', group: null, backend: 'q' },
  city: { kind: 'list', url: 'city', group: 'location', backend: 'custom' },
  registerId: { kind: 'text', url: 'registerId', group: 'register', backend: 'custom' },
  registerCourt: { kind: 'text', url: 'registerCourt', group: 'register', backend: 'custom' },
  registerType: { kind: 'text', url: 'registerType', group: 'register', backend: 'registerType' },
  euid: { kind: 'text', url: 'euid', group: 'register', backend: 'custom' },
  hasYearlyMetadata: { kind: 'flag', url: 'hasYearlyMetadata', group: 'size', backend: 'hasYearlyMetadata' },
  Bundesland: { kind: 'list', url: 'Bundesland', group: 'location', backend: 'custom' },
  revenueMin: { kind: 'bound', url: 'revenueMin', group: 'size', backend: 'custom', metric: 'revenue' },
  revenueMax: { kind: 'bound', url: 'revenueMax', group: 'size', backend: 'custom', metric: 'revenue' },
  aktivaMin: { kind: 'bound', url: 'aktivaMin', group: 'size', backend: 'custom', metric: 'aktiva' },
  aktivaMax: { kind: 'bound', url: 'aktivaMax', group: 'size', backend: 'custom', metric: 'aktiva' },
  profitMin: { kind: 'bound', url: 'profitMin', group: 'size', backend: 'custom', metric: 'profit' },
  profitMax: { kind: 'bound', url: 'profitMax', group: 'size', backend: 'custom', metric: 'profit' },
  employeesMin: { kind: 'bound', url: 'employeesMin', group: 'size', backend: 'custom', metric: 'employees' },
  employeesMax: { kind: 'bound', url: 'employeesMax', group: 'size', backend: 'custom', metric: 'employees' },
  // Widens the revenue interval, so it is written only beside a revenue bound
  // and travels inside the request's `revenue` range.
  revenueIncludeNull: {
    kind: 'flag', url: 'revenueIncludeNull', group: 'size', backend: 'custom', modifier: true,
    write: (params, f) => {
      if (f.revenueIncludeNull && (f.revenueMin !== undefined || f.revenueMax !== undefined)) params.set('revenueIncludeNull', 'true');
    },
  },
  ageRanges: { kind: 'list', url: 'ageRange', group: 'people', backend: 'custom' },
  naceCodes: { kind: 'list', url: 'naceCode', group: 'industry', backend: 'custom' },
  industries: { kind: 'list', url: 'industry', group: 'industry', backend: 'industries' },
  foundedAtFrom: { kind: 'text', url: 'foundedAtFrom', group: 'profile', backend: 'custom' },
  foundedAtTo: { kind: 'text', url: 'foundedAtTo', group: 'profile', backend: 'custom' },
  tags: { kind: 'list', url: 'tag', group: 'industry', backend: 'tags' },
  personName: { kind: 'text', url: 'person_name', group: 'people', backend: 'personName' },
  rechtsformCodes: {
    kind: 'list', url: 'rechtsformCode', group: 'profile', backend: 'rechtsformCodes',
    // One picker option can stand for several codes ("690000,692320").
    toBackend: (codes) => codes.flatMap((c) => c.split(',')),
  },
  cpvCode: { kind: 'text', url: 'cpvCode', group: 'industry', backend: 'cpvCode' },
  memberships: { kind: 'list', url: 'membership', group: 'profile', backend: 'memberships' },
  legalStatus: { kind: 'list', url: 'legalStatus', group: 'profile', backend: 'legalStatus' },
  // Rendered in the profile group, but its header count and Clear have never
  // included it (nor has the search_performed count) — kept as it was.
  companySize: { kind: 'list', url: 'companySize', group: null, backend: 'companySize' },
  // Every country is the default ('') and stays out of the URL; an explicit
  // DE / CH / AT (old links, saved searches) still narrows. 'all' is the
  // legacy spelling of the default.
  country: {
    kind: 'text', url: 'country', group: null, backend: 'country',
    read: (params, fallback) => {
      if (!params.has('country')) return fallback;
      const c = params.get('country').toUpperCase();
      return isCompanyCountryCode(c) ? c : '';
    },
  },
  tenderRole: { kind: 'list', url: 'tenderRole', group: 'profile', backend: 'tenderRole' },
  familyManaged: { kind: 'triState', url: 'familyManaged', group: 'people', backend: 'familyManaged' },
  founderLed: { kind: 'triState', url: 'founderLed', group: 'people', backend: 'founderLed' },
  successionRisk: { kind: 'triState', url: 'successionRisk', group: 'people', backend: 'successionRisk' },
  leiCode: { kind: 'text', url: 'leiCode', group: 'register', backend: 'leiCode' },
  // State holds the digit tail only — the UI shows a fixed "DE" prefix and the
  // request prepends it. A leading DE from an external URL is stripped so it
  // is not doubled.
  ustIdnr: {
    kind: 'text', url: 'ustIdnr', group: 'register', backend: 'ustIdnr',
    read: (params, fallback) => (params.get('ustIdnr') || fallback || '').replace(/^DE/i, '').replace(/\D/g, ''),
    toBackend: (digits) => `DE${digits}`,
  },
  hasWebsite: { kind: 'flag', url: 'hasWebsite', group: 'profile', backend: 'hasWebsite' },
  hasEmail: { kind: 'flag', url: 'hasEmail', group: 'profile', backend: 'hasEmail' },
} as const satisfies FilterDefs;

/** Every FilterParams key, in URL order. */
export const FILTER_KEYS = Object.keys(FILTER_DEFS) as FilterKey[];

/** One field's definition, for the generic engines. */
export function filterDef(key: FilterKey): AnyFilterDef {
  return FILTER_DEFS[key];
}

/** Request keys the generic mapping produces (the rest are 'custom'). */
export type GenericBackendKey = Exclude<(typeof FILTER_DEFS)[FilterKey]['backend'], 'custom'>;

/** Generic request keys and the kind that decides whether each is sent. */
export const GENERIC_BACKEND_FIELDS = FILTER_KEYS.flatMap((key) => {
  const def = filterDef(key);
  return def.backend === 'custom' ? [] : [{ key: def.backend as GenericBackendKey, kind: def.kind }];
});

/** Filters where `false` selects companies rather than disabling the filter. */
export const TRI_STATE_KEYS: ReadonlySet<string> = new Set(
  FILTER_KEYS.filter((key) => filterDef(key).kind === 'triState'),
);

const EMPTY_BY_KIND: Record<FilterKind, () => unknown> = {
  flag: () => false,
  triState: () => undefined,
  text: () => '',
  list: () => [],
  bound: () => undefined,
  year: () => undefined,
};

/** The field's "no filter" value (a fresh array for lists). */
export function emptyFilterValue<K extends FilterKey>(key: K): FilterParams[K] {
  return EMPTY_BY_KIND[filterDef(key).kind]() as FilterParams[K];
}

/** Every filter at its "no filter" value, in URL order. */
export function emptyFilterParams(): FilterParams {
  return Object.fromEntries(FILTER_KEYS.map((key) => [key, emptyFilterValue(key)])) as unknown as FilterParams;
}

/** The other side of a `bound` field's interval. */
function otherBound(key: FilterKey): FilterKey {
  return (key.endsWith('Min') ? key.replace(/Min$/, 'Max') : key.replace(/Max$/, 'Min')) as FilterKey;
}

/**
 * Whether one field narrows the search, as the sidebar counts it: lists count
 * once however many values they hold, an interval counts once on its `Min`
 * side, a modifier never.
 */
export function countsAsActive(key: FilterKey, f: FilterParams): boolean {
  const def = filterDef(key);
  if (def.modifier) return false;
  const value: unknown = f[key];
  switch (def.kind) {
    case 'list':
      return (value as string[]).length > 0;
    case 'triState':
      return value !== undefined;
    case 'year':
      return value != null;
    case 'bound':
      return key.endsWith('Min') && (value !== undefined || f[otherBound(key)] !== undefined);
    default:
      return Boolean(value);
  }
}

/** The keys a sidebar group's header counts and its Clear resets. */
export function groupFilterKeys(group: FilterGroupId): FilterKey[] {
  return FILTER_KEYS.filter((key) => filterDef(key).group === group);
}

/** Active filters per sidebar group (the group header badges). */
export function countFiltersByGroup(f: FilterParams): Record<FilterGroupId, number> {
  const counts = Object.fromEntries(FILTER_GROUP_IDS.map((g) => [g, 0])) as Record<FilterGroupId, number>;
  for (const key of FILTER_KEYS) {
    const group = filterDef(key).group;
    if (group && countsAsActive(key, f)) counts[group] += 1;
  }
  return counts;
}

/**
 * How many filter values a submitted search carries (the search_performed
 * event): every value of a list, one per other active field, filters outside
 * the sidebar groups (keyword, country scope, company size) not at all.
 */
export function submittedFilterCount(f: FilterParams): number {
  let count = 0;
  for (const key of FILTER_KEYS) {
    const def = filterDef(key);
    if (!def.group || def.notInSubmittedCount || !countsAsActive(key, f)) continue;
    count += def.kind === 'list' ? (f[key] as string[]).length : 1;
  }
  return count;
}
